import * as React from 'react';
import 'moment/locale/pt';
import { Notes } from '@/components/custom/notes';
import { Notifications } from '@/components/custom/notifications';
import usePermission from '@/hooks/usePermission';
import { NotificationsMessage } from '@/components/custom/notifications-message';
import { useTranslation } from 'react-i18next';
import Search from '@/app/components/Search';
import EnvironmentSelector from '@/app/components/EnvironmentSelector';
import { cn } from '@/lib/utils';

/**
 * Header Principal
 * Gerencia o posicionamento fixo, padding e variações de altura.
 * Classes BEM: c-header
 */
const HeaderWrapper = React.forwardRef(({ className, hasCustomLogos, ...props }, ref) => (
  <header
    ref={ref}
    className={cn('c-header-wrapper', 'fixed inset-x-0 top-0 z-[104] flex items-center bg-neutral-bright', '!p-[4.2em_1rem_1rem_1rem]', 'md:left-[64px] md:h-[88px] md:!p-[36px_0_0_0]', hasCustomLogos ? 'top-[68px] md:top-0' : 'top-0', className)}
    {...props}
  />
));
HeaderWrapper.displayName = 'HeaderWrapper';

/**
 * Header Container
 * Responsável pelo alinhamento interno e a borda decorativa inferior.
 * Classes BEM: c-header-container
 */
const HeaderContainer = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('c-header-container', 'flex w-full items-center justify-end', 'md:mx-[45px] md:h-[52px] md:border-b md:border-solid md:border-primary-lightest', className)}
    {...props}
  />
));
HeaderContainer.displayName = 'HeaderContainer';

/**
 * Header Actions
 * Agrupa as ações do lado direito do header.
 * Classes BEM: c-header-actions
 */
const HeaderActions = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('c-header-actions', 'mr-[36px] flex min-h-[28px] items-center gap-5', 'sm:gap-6', 'md:mr-0', className)}
    {...props}
  />
));
HeaderActions.displayName = 'HeaderActions';

export function Header({ hasCustomLogos }) {
  const { i18n } = useTranslation();

  React.useEffect(() => {
    i18n.changeLanguage(process.env.REACT_APP_LOCALE);
  }, []);

  const showNotes = usePermission('Criar anotações', 'Anotações') || usePermission('Editar anotações', 'Anotações') || usePermission('Excluir anotações', 'Anotações') || usePermission('Visualizar anotações', 'Anotações');

  return (
    <HeaderWrapper hasCustomLogos={hasCustomLogos}>
      <HeaderContainer>
        <HeaderActions>
          <EnvironmentSelector />
          <Search />
          <NotificationsMessage />
          <Notifications />
        </HeaderActions>

        {showNotes && <Notes hasCustomLogos={hasCustomLogos} />}
      </HeaderContainer>
    </HeaderWrapper>
  );
}
